import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useDateFilter } from "@/context/DateFilterContext";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  ResponsiveContainer, ScatterChart, Scatter, XAxis, YAxis, ZAxis, CartesianGrid, Tooltip, Cell,
  PieChart, Pie, BarChart, Bar,
} from "recharts";
import { Target, Lightbulb, FlaskConical, Rocket, XCircle, Gauge, TrendingUp } from "lucide-react";

const METRICS = [
  { key: "active_goals", label: "Active Goals", icon: Target, color: "text-emerald-600 bg-emerald-50" },
  { key: "ideas_scouted", label: "Ideas Scouted", icon: Lightbulb, color: "text-amber-600 bg-amber-50" },
  { key: "in_validation", label: "In Validation", icon: FlaskConical, color: "text-violet-600 bg-violet-50" },
  { key: "approved_rollout", label: "Approved / Rollout", icon: Rocket, color: "text-sky-600 bg-sky-50" },
  { key: "ignored", label: "Ignored / Rejected", icon: XCircle, color: "text-red-600 bg-red-50" },
];

const PIE_COLORS = ["#10B981", "#06B6D4", "#f59e0b", "#8b5cf6", "#ef4444", "#0ea5e9"];

export default function Dashboard() {
  const { start, end } = useDateFilter();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/analytics/dashboard", { params: { start, end } }).then((r) => setData(r.data)).catch(() => {});
  }, [start, end]);

  if (!data) return <div className="text-muted-foreground">Loading dashboard...</div>;
  const m = data.metrics;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div>
        <h1 className="font-head text-3xl font-extrabold tracking-tight">Executive Dashboard</h1>
        <p className="text-muted-foreground">Innovation velocity & OKR attainment across Youngness Center and Awish Clinic.</p>
      </div>

      {/* Metrics ribbon */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5" data-testid="metrics-ribbon">
        {METRICS.map((mt) => (
          <Card key={mt.key} className="p-4 border-slate-200 shadow-sm transition-transform hover:-translate-y-0.5">
            <div className={`mb-3 inline-grid h-9 w-9 place-items-center rounded-lg ${mt.color}`}><mt.icon size={18} /></div>
            <p className="text-3xl font-head font-extrabold tracking-tight" data-testid={`metric-${mt.key}`}>{m[mt.key]}</p>
            <p className="text-xs text-muted-foreground">{mt.label}</p>
          </Card>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* OKR attainment */}
        <Card className="p-6 border-slate-200 shadow-sm lg:col-span-1">
          <div className="flex items-center gap-2 text-slate-700"><Gauge size={18} className="text-primary" /><h3 className="font-head font-bold">OKR Attainment</h3></div>
          <div className="mt-6 text-center">
            <p className="font-head text-6xl font-extrabold text-primary" data-testid="okr-attainment">{m.avg_goal_progress}%</p>
            <p className="mt-1 text-sm text-muted-foreground">Avg. goal progress (selected period)</p>
          </div>
          <Progress value={m.avg_goal_progress} className="mt-6 h-3" data-testid="okr-progress-bar" />
          <div className="mt-6 flex items-center justify-center gap-2 rounded-lg bg-slate-50 py-3 text-sm">
            <TrendingUp size={16} className="text-secondary" />
            <span className="text-muted-foreground">Discovery → Approval velocity:</span>
            <span className="font-semibold text-slate-800" data-testid="velocity-metric">{m.velocity_days} days</span>
          </div>
        </Card>

        {/* Category distribution */}
        <Card className="p-6 border-slate-200 shadow-sm lg:col-span-1">
          <h3 className="font-head font-bold text-slate-700">Category Distribution</h3>
          <p className="text-xs text-muted-foreground mb-2">Devices vs Formulations vs Services vs Biotech</p>
          <ResponsiveContainer width="100%" height={230}>
            <PieChart>
              <Pie data={data.category_distribution} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} innerRadius={45} paddingAngle={2}>
                {data.category_distribution.map((e, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap gap-2 justify-center text-xs">
            {data.category_distribution.map((c, i) => (
              <span key={i} className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />{c.name}</span>
            ))}
          </div>
        </Card>

        {/* Goal status */}
        <Card className="p-6 border-slate-200 shadow-sm lg:col-span-1">
          <h3 className="font-head font-bold text-slate-700">Goal Status</h3>
          <p className="text-xs text-muted-foreground mb-2">OKR health breakdown</p>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={Object.entries(data.goal_status).map(([k, v]) => ({ name: k, value: v }))} layout="vertical" margin={{ left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" allowDecimals={false} fontSize={11} />
              <YAxis type="category" dataKey="name" width={70} fontSize={11} />
              <Tooltip />
              <Bar dataKey="value" fill="#10B981" radius={[0, 6, 6, 0]} barSize={18} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Impact vs Feasibility matrix */}
      <Card className="p-6 border-slate-200 shadow-sm">
        <h3 className="font-head font-bold text-slate-700">Impact vs. Feasibility Matrix</h3>
        <p className="text-xs text-muted-foreground mb-2">High-impact, low-effort ideas (top-left) are your quick wins.</p>
        <ResponsiveContainer width="100%" height={360}>
          <ScatterChart margin={{ top: 20, right: 30, bottom: 30, left: 10 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" dataKey="effort" name="Effort" domain={[0, 6]} ticks={[1, 2, 3, 4, 5]} label={{ value: "Effort / Feasibility →", position: "bottom", fontSize: 12 }} fontSize={11} />
            <YAxis type="number" dataKey="impact" name="Impact" domain={[0, 6]} ticks={[1, 2, 3, 4, 5]} label={{ value: "Impact →", angle: -90, position: "insideLeft", fontSize: 12 }} fontSize={11} />
            <ZAxis range={[120, 120]} />
            <Tooltip cursor={{ strokeDasharray: "3 3" }} content={({ payload }) => payload?.[0] ? (
              <div className="rounded-lg border bg-white p-2 text-xs shadow-md">
                <p className="font-semibold">{payload[0].payload.title}</p>
                <p className="text-muted-foreground">Impact {payload[0].payload.impact} · Effort {payload[0].payload.effort}</p>
                <p className="text-muted-foreground">{payload[0].payload.stage}</p>
              </div>) : null} />
            <Scatter data={data.matrix} fill="#10B981">
              {data.matrix.map((e, i) => (
                <Cell key={i} fill={e.impact >= 4 && e.effort <= 2 ? "#10B981" : e.impact >= 4 ? "#06B6D4" : "#94a3b8"} />
              ))}
            </Scatter>
          </ScatterChart>
        </ResponsiveContainer>
      </Card>
    </div>
  );
}
